import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { formatDate } from "~/utils/date-formatter";
import idFormatter from "~/utils/id-formatter";
import { Printer, Download, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { apiClient } from "~/lib/orpc-client";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { formatReceiptId } from "~/utils/receipt-id-formatter";
import { formatPrice } from "~/utils/format-price";

export const Route = createFileRoute("/receipt/$applicationId")({
  component: ReceiptPage,
});

const BUSINESS_INFO = {
  name: "23 First Corporation trading as Proudly Human",
  abn: "52 689 777 062",
  address: "Melbourne, VIC Australia",
  website: "proudlyhuman.org",
};

function getReceiptApplicationTitle(application: { category: string | null; title: string | null } | null | undefined) {
  if (application?.category === "WRITER") return "Writer";
  return application?.title || "Untitled Application";
}

function ReceiptPage() {
  const { applicationId } = Route.useParams();
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: transactionData, isLoading: isLoadingTransaction } = useQuery({
    queryKey: QUERY_KEYS.TRANSACTION_BY_APPLICATION_ID(applicationId),
    queryFn: () => apiClient.applications.getTransaction({ id: Number(applicationId) }),
  });

  const { data: applicationData, isLoading: isLoadingApplication } = useQuery({
    queryKey: QUERY_KEYS.APPLICATION_BY_ID(applicationId),
    queryFn: () => apiClient.applications.getById({ id: Number(applicationId) }),
  });

  const isLoading = isLoadingTransaction || isLoadingApplication;
  const transaction = transactionData?.[0];
  const application = applicationData;

  const getCardBrandDisplay = (brand: string) => {
    const brandMap: Record<string, string> = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
    };
    return brandMap[brand.toLowerCase()] || brand;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current || !transaction) return;

    setIsDownloading(true);

    const filename = `receipt-${formatReceiptId(transaction.id)}.pdf`;

    // CSS overrides to replace oklch colors with RGB equivalents
    const colorOverrides = `
      * {
        --background: #ffffff !important;
        --foreground: #1a1a1a !important;
        --muted: #f5f5f5 !important;
        --muted-foreground: #737373 !important;
        --border: #e5e5e5 !important;
        --brand-green-primary: #5B8578 !important;
        --brand-green-dark: #3d5a4f !important;
        --brand-green-light: #e8f0ed !important;
      }
      .bg-brand-green-primary { background-color: #5B8578 !important; }
      .bg-brand-green-light\\/30 { background-color: rgba(232, 240, 237, 0.3) !important; }
      .bg-brand-green-light\\/50 { background-color: rgba(232, 240, 237, 0.5) !important; }
      .text-brand-green-primary { color: #5B8578 !important; }
      .text-brand-green-dark { color: #3d5a4f !important; }
      .text-muted-foreground { color: #737373 !important; }
      .text-foreground { color: #1a1a1a !important; }
      .bg-gray-50 { background-color: #f9fafb !important; }
      .bg-white { background-color: #ffffff !important; }
      .border-gray-100 { border-color: #f3f4f6 !important; }
      .border-border { border-color: #e5e5e5 !important; }
    `;

    const options = {
      margin: 0.5,
      filename,
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        onclone: (clonedDoc: Document) => {
          const style = clonedDoc.createElement("style");
          style.textContent = colorOverrides;
          clonedDoc.head.appendChild(style);
        },
      },
      jsPDF: { unit: "in" as const, format: "a4" as const, orientation: "portrait" as const },
    };

    try {
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default;
      await html2pdf().set(options).from(receiptRef.current).save();
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 print:bg-white print:p-0">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden print:shadow-none print:rounded-none">
          <div className="bg-brand-green-primary px-8 py-6">
            <Skeleton className="h-12 w-48 bg-white/20" />
          </div>
          <div className="px-8 py-6 space-y-6">
            <div className="flex justify-between">
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-12 w-32" />
            </div>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-8 py-12 text-center">
            <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Receipt Not Found</h2>
            <p className="text-muted-foreground">
              No payment receipt was found for this application.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Global print styles to hide layout elements */}
      <style>{`
        @media print {
          header, nav, aside, footer, .sidebar, [data-sidebar], [role="navigation"] {
            display: none !important;
          }
          body, html, #root, main {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5in;
            size: A4;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gray-50 py-8 px-4 print:min-h-0 print:bg-white print:p-0">
        {/* Print/Download Actions - Hidden when printing */}
        <div className="max-w-2xl mx-auto mb-6 flex justify-end gap-3 print:hidden">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print Receipt
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} disabled={isDownloading}>
            {isDownloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>

        {/* Receipt Card */}
        <div ref={receiptRef} className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden print:max-w-none print:mx-0 print:rounded-none print:shadow-none print:overflow-visible">
          {/* Header with Brand */}
          <div className="bg-brand-green-primary px-8 py-6 text-white print:bg-[#5B8578] print:text-white print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
            <div className="flex items-center justify-between">
              <img
                src="/logo/flat/LOGO-WHITE.png"
                alt="Proudly Human"
                className="h-10 w-auto"
              />
              <div className="text-right">
                <h1 className="text-xl font-semibold">Payment Receipt</h1>
              </div>
            </div>
          </div>

          {/* Receipt Content */}
          <div className="px-8 py-6 print:px-0 print:py-6">
            {/* Receipt Number & Date */}
            <div className="flex justify-between items-end mb-8">
              <div>
                <p className="text-sm text-muted-foreground print:text-gray-500">Receipt Number</p>
                <p className="font-mono font-semibold text-lg print:text-black">
                  {formatReceiptId(transaction.id)}
                </p>
              </div>
              <p className="font-semibold text-lg print:text-black">
                {transaction.createdAt ? formatDate(transaction.createdAt.toString()) : "N/A"}
              </p>
            </div>

            <Separator className="my-6 print:border-gray-300" />

            {/* Amount Section */}
            <div className="bg-brand-green-light/30 rounded-xl p-6 mb-6 print:bg-[#f0f7f5] print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground mb-1 print:text-gray-500">Amount Paid</p>
                  <p className="text-4xl font-bold text-brand-green-dark print:text-[#5B8578]">
                    {formatPrice(transaction.amount, transaction.currency)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1 print:text-gray-500">Payment Method</p>
                  <p className="font-semibold print:text-black">
                    {getCardBrandDisplay(transaction.cardBrand)} ••••{" "}
                    {transaction.cardLast4}
                  </p>
                </div>
              </div>
            </div>

            {/* Billed To */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 print:text-gray-500">
                Billed To
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-semibold print:text-black">
                    {[transaction.customerFirstName, transaction.customerLastName]
                      .filter(Boolean)
                      .join(" ") || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="font-semibold print:text-black">{transaction.customerEmail}</p>
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 print:bg-[#f9f9f9] print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
                <p className="text-lg font-semibold text-foreground print:text-black">
                  {getReceiptApplicationTitle(application)}
                </p>
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider print:text-gray-500">Application ID</p>
                  <p className="text-sm font-mono print:text-black">
                    {idFormatter(Number(applicationId))}
                  </p>
                </div>
              </div>
            </div>

            <Separator className="my-6 print:border-gray-300" />

            {/* Line Items */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 print:text-gray-500">
                Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between print:text-black">
                  <span>Certification Application Fee</span>
                  <span>{formatPrice(transaction.amount, transaction.currency)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground print:text-gray-500">
                  <span>GST (included)</span>
                  <span>{formatPrice(Math.round(transaction.amount / 11), transaction.currency)}</span>
                </div>
                <Separator className="print:border-gray-300" />
                <div className="flex justify-between font-bold text-lg print:text-black">
                  <span>Total</span>
                  <span>{formatPrice(transaction.amount, transaction.currency)}</span>
                </div>
              </div>
            </div>

            {/* Business Footer */}
            <div className="text-center text-sm text-muted-foreground pt-4 border-t print:text-gray-600 print:border-gray-300">
              <p className="font-semibold text-foreground print:text-black">{BUSINESS_INFO.name}</p>
              <p>ABN: {BUSINESS_INFO.abn}</p>
              <p>{BUSINESS_INFO.address}</p>
              <p className="text-brand-green-primary mt-2 print:text-[#5B8578]">{BUSINESS_INFO.website}</p>
            </div>
          </div>

          {/* Footer Banner */}
          <div className="bg-brand-green-light/50 px-8 py-4 text-center print:bg-[#f0f7f5] print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
            <p className="text-sm text-brand-green-dark print:text-[#5B8578]">
              Thank you for choosing ProudlyHuman™
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
